import { AngularGridInstance, Column } from 'angular-slickgrid';
import { normalizeEmptyNumericFields } from '../../utils/normalize-utils';

export function patchEditorForRequiredDependencies(grid: AngularGridInstance | undefined, data: any): void {
  if (!grid?.slickGrid) return;

  const row = data?.args?.row;
  const cell = data?.args?.cell;

  setTimeout(() => {
    const slickGrid = grid.slickGrid;
    const activeCell = slickGrid.getActiveCell?.();
    const targetRow = Number.isFinite(row) ? row : activeCell?.row;
    const targetCell = Number.isFinite(cell) ? cell : activeCell?.cell;

    if (!Number.isFinite(targetRow) || !Number.isFinite(targetCell)) return;

    const columns = slickGrid.getColumns?.() ?? [];
    const column = columns[targetCell as number] as Column | undefined;
    if (!column?.params?.forceValidateOnBlur) return;

    const editor = slickGrid.getCellEditor?.();
    const editorAny = editor as any;
    if (!editorAny || typeof editorAny.isValueChanged !== 'function') return;

    if (editorAny.__forceValidateOnBlurPatched) return;

    const originalIsValueChanged = editorAny.isValueChanged.bind(editorAny);
    editorAny.isValueChanged = () => {
      const baseChanged = originalIsValueChanged();
      if (baseChanged) return true;

      const validator = column.validator;
      if (!validator) return false;

      const fieldName = column.field || '';
      let value: any;

      if (typeof editorAny.serializeValue === 'function') {
        value = editorAny.serializeValue();
      } else if (typeof editorAny.getValue === 'function') {
        value = editorAny.getValue();
      } else if (fieldName) {
        value = editorAny.args?.item?.[fieldName];
      }

      const item = editorAny.args?.item || {};
      const updatedItem = fieldName ? { ...item, [fieldName]: value } : item;
      const validationArgs = { ...editorAny.args, item: updatedItem };
      const result = validator(value, validationArgs);

      return result?.valid === false;
    };

    editorAny.__forceValidateOnBlurPatched = true;
  }, 0);
}

export function validateEditorBeforeDestroy(
  grid: AngularGridInstance | undefined,
  data: any,
  showError: (msg: string) => void,
  enableSingleClickEdit: () => void
): boolean {
  if (!grid?.slickGrid) return true;

  const editor = data?.args?.editor as any;
  const column = data?.args?.column ?? editor?.args?.column ?? editor?.columnDef;
  const row = data?.args?.row;
  const cell = data?.args?.cell;
  const activeCell = grid.slickGrid.getActiveCell?.();
  const fallbackRow = Number.isFinite(row) ? row : activeCell?.row;
  const fallbackCell = Number.isFinite(cell) ? cell : activeCell?.cell;

  if (editor) {
    let validationResult: { valid: boolean; msg: string } | undefined;

    if (typeof editor.validate === 'function') {
      validationResult = editor.validate();
    } else if (column?.validator) {
      const fieldName = column.field || '';
      let value: any;

      if (typeof editor.serializeValue === 'function') {
        value = editor.serializeValue();
      } else if (typeof editor.getValue === 'function') {
        value = editor.getValue();
      } else if (fieldName) {
        value = editor.args?.item?.[fieldName];
      }

      const item = editor.args?.item || {};
      const updatedItem = fieldName ? { ...item, [fieldName]: value } : item;
      const validationArgs = { ...editor.args, item: updatedItem };
      validationResult = column.validator(value, validationArgs);
    }

    if (validationResult && validationResult.valid === false) {
      showError(validationResult.msg);

      setTimeout(() => {
        if (!grid?.slickGrid) return;
        enableSingleClickEdit();
        if (Number.isFinite(fallbackRow) && Number.isFinite(fallbackCell)) {
          grid.slickGrid.setActiveCell(fallbackRow as number, fallbackCell as number, true);
          grid.slickGrid.editActiveCell?.();
        }
      });
      return false;
    }
  }

  return true;
}

export function applyPostEditNormalization(data: any): void {
  const editorAny = data?.args?.editor as any;
  const item = data?.args?.item ?? editorAny?.args?.item ?? editorAny?.item;
  const column = data?.args?.column ?? editorAny?.args?.column ?? editorAny?.columnDef;
  const fieldName = column?.field;

  if (!item || !fieldName) return;

  if (typeof item[fieldName] === 'string') {
    const trimmedValue = item[fieldName].trim();
    item[fieldName] = trimmedValue;

    if (editorAny?.setValue) {
      editorAny.setValue(trimmedValue);
    }
  }

  if (column?.type === 'number') {
    const normalized = normalizeEmptyNumericFields(item, [fieldName]);
    if (normalized !== item) {
      Object.assign(item, normalized);
    }
  }
}
